import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CORE_CONFIG_TOKEN } from '@ndk/ng-core/component/core.config';
import { NDDTranslationService } from '@ndk/ng-translation';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';

import { ICoreConfigInternal } from '@core/core.config.model';
import { DownloadService } from '@core/download/download.service';
import { LoggerService } from '@core/logger/logger.service';

import { ISettingsScanFlowBehaviorModel, ISettingsScanFlowFtpCommand, ISettingsScanFlowFtpModel, ISettingsScanFlowSharepointCommand, ISettingsScanFlowSharepointModel, ISettingsScanFlowSharepointTestConnectionCommand, ISettingsScanFlowSharepointTestConnectionUsingExistingConfigurationCommand, SettingsScanFlowSharepointTestConnectionResultType } from './models/settings-scan-flow.model';

@Injectable({
    providedIn: 'root',
})
export class SettingsScanFlowService {
    private apiUrl: string;
    private refreshShowScanFlow$: Subject<ISettingsScanFlowBehaviorModel> = new Subject<ISettingsScanFlowBehaviorModel>();

    constructor(
        @Inject(CORE_CONFIG_TOKEN) config: ICoreConfigInternal,
        private http: HttpClient,
        private loggerService: LoggerService,
        private translation: NDDTranslationService,
        private downloadService: DownloadService,
    ) {
        this.apiUrl = `${ config.apiEndpoint }api/settings-scan-flow`;
    }

    public getBehavior(): Observable<ISettingsScanFlowBehaviorModel> {
        return this.http.get<ISettingsScanFlowBehaviorModel>(`${ this.apiUrl }/behavior`);
    }

    public updateBehavior(value: boolean): Observable<boolean> {
        const command: any = { scanFlowEnabled: value };

        return this.http.post<boolean>(`${ this.apiUrl }/behavior`, command)
            .pipe(tap(() => this.loggerService.success(true, [this.translation.instant('360SettingsSuccess')])));
    }

    public checkForScanFlowSettingsChanges(): Observable<ISettingsScanFlowBehaviorModel> {
        return this.refreshShowScanFlow$.asObservable();
    }

    public refreshScanFlowSettings(value: boolean): void {
        this.refreshShowScanFlow$.next({ scanFlowEnabled: value });
    }

    public getScanFlowFtpSettings(): Observable<ISettingsScanFlowFtpModel> {
        return this.http.get<ISettingsScanFlowFtpModel>(`${ this.apiUrl }/ftp`);
    }

    public updateFtpSettings(command: ISettingsScanFlowFtpCommand): Observable<boolean> {
        return this.http.post<boolean>(`${ this.apiUrl }/ftp`, command)
            .pipe(tap(() => this.loggerService.success(true, [this.translation.instant('360SettingsSuccess')])));
    }

    public getScanFlowIntegrationManualAddress(): Observable<string> {
        return this.http.get<string>(`${ this.apiUrl }/manual`);
    }

    public checkFtpSettingUsedByFlows(ftpSettingId: number): Observable<boolean> {
        return this.http.get<boolean>(`${ this.apiUrl }/check-ftp-use/${ ftpSettingId }`);
    }

    public getScanFlowSharepointSettings(): Observable<ISettingsScanFlowSharepointModel> {
        return this.http.get<ISettingsScanFlowSharepointModel>(`${ this.apiUrl }/sharepoint`);
    }

    public updateSharepointSettings(command: ISettingsScanFlowSharepointCommand): Observable<boolean> {
        return this.http.post<boolean>(`${ this.apiUrl }/sharepoint`, command)
            .pipe(tap(() => this.loggerService.success(true, [this.translation.instant('360SettingsSuccess')])));
    }

    public checkSharepointSettingUsedByFlows(sharepointSettingId: number): Observable<boolean> {
        return this.http.get<boolean>(`${ this.apiUrl }/check-sharepoint-use/${ sharepointSettingId }`);
    }

    public sharepointTestConnection(command: ISettingsScanFlowSharepointTestConnectionCommand)
        : Observable<SettingsScanFlowSharepointTestConnectionResultType> {
        return this.http.post<SettingsScanFlowSharepointTestConnectionResultType>(`${ this.apiUrl }/sharepoint-test-connection`, command);
    }

    public sharepointTestConnectionUsingExistingConfiguration(command: ISettingsScanFlowSharepointTestConnectionUsingExistingConfigurationCommand)
        : Observable<SettingsScanFlowSharepointTestConnectionResultType> {
        return this.http.post<SettingsScanFlowSharepointTestConnectionResultType>(
            `${ this.apiUrl }/sharepoint-test-connection-with-existing-config`, command);
    }

    public getOrCreateSharepointCertificate(): Observable<any> {
       return this.downloadService.download(`${ this.apiUrl }/get-or-create-sharepoint-certificate`, {}, `NDD Print Sharepoint Integration.cer`);
    }
}
